import { reduceToObject, twoWayMapping } from "./shared.js";

// NOTE: these are isolated from `survey.models.Survey.PRODUCT_nnn` via an
//       indirect 'product_key' added by ``webapp.view_data.survey_info`.
export const PRODUCT_KEYS = {
  _5E: "5e", // ~WORKAROUND: this isn't _internal, just better if not leading number…
  BEL: "bel",
  BEL4T: "bel4t",
  ECE: "ece",
};

const S = PRODUCT_KEYS; // typical alias (for clearer usage don't export this directly)…

export const INCLUDE_NA_GROUPS = false;

// ~mimics jQuery data auto-parsing
function parseEntry(obj, k) {
  let v = obj[k];
  try {
    v = JSON.parse(v);
  } catch (e) {}
  return v;
}

// replacement for jQuery `$(qry).data(key)`
export function getElementData(qry, key = null) {
  let el = document.querySelector(qry);
  if (!el) return null;
  else if (key) return parseEntry(el.dataset, key);
  else {
    let _d = el.dataset;
    return reduceToObject(Object.keys(_d), (k) => [k, parseEntry(_d, k)]);
  }
}

export function rankInfo(raw_ranks, raw_ranges) {
  const _RANK = reduceToObject(raw_ranks, (d) => [d.code, d]); // mapping for e.g. `RANK[1].long_label`
  const allRanks = Array.from("X012345")
    .map((k) => ({ code: k, ..._RANK[k] }))
    .reverse();
  const rankRanges = raw_ranges.map((d, i) => ({
    rank: _RANK[i + 1],
    range: d,
  }));

  const _rankBreaks = raw_ranges.map((d, i) => [i + 1, d[1]]);
  _rankBreaks.unshift([0, 0]); // we now need rank-0 for overall text
  function scoreToRankCode(score) {
    if (score == null) code = "X";
    else
      for (var [code, cutoff] of _rankBreaks) {
        if (score < cutoff) break;
      }
    return [code, _RANK[code]];
  }

  return { scoreToRankCode, allRanks, rankRanges };
}

export function generateMetadata(_level, d) {
  let _alias,
    _child_type = null;
  switch (_level) {
    case "essentials":
      switch (d.slug) {
        case "overall":
          _alias = "5e_overall";
          _child_type = "essentials";
          break;
        case "_supplemental":
          _alias = "supplementals";
          _child_type = "measures";
          break;
        default:
          _child_type = "measures";
      }
      break;
    case "measures":
      switch (d.slug) {
        case "all":
          _alias = "all_measures";
          _child_type = "measures";
          break;
        default:
          _child_type = "items";
      }
      break;
  }
  return { _alias, _level, _child_type, _children: [] };
}

export function extractResults({ survey, scoreToRankCode }, variant, d) {
  let results = survey._fullYears.map((survey) => {
    let _pair =
      d.data && d.data[variant] && d.data[variant][survey._score_index];
    let [score, newref] = _pair || [];
    let [code, rank] = scoreToRankCode(score);
    return { score, newref, code, rank, survey };
  });
  results.reverse(); // HACK: simplify the assignments/calculations below
  results.latest = results[0];
  results.latest._change =
    results.length >= 2 && results[0].score != null && results[1].score != null
      ? results[0].score - results[1].score
      : null;
  results.reverse();
  return results;
}

export function expandComparisonFamilies(rawComparisons) {
  let comparisonFamilies = {};
  Object.keys(rawComparisons).forEach((_level) => {
    comparisonFamilies[_level] = expandFamily(rawComparisons[_level]);
  });
  return comparisonFamilies;

  function expandFamily(rawFamily) {
    let comparisons = rawFamily
      .map(([name, slug, rawVariants]) => {
        let variants = rawVariants.map(([label, slug]) => ({ slug, label }));
        variants.forEach((v) => {
          // special UI labelling variants (used for BEL disaggregation)
          // (`option` is used by BelDisaggregations, `group` is used by BelTable…
          // …obfuscated via renaming switcheroo stuff of _baseDisaggregationGroups)
          switch (slug) {
            case "grade":
              v.option = v.label.replace(" Graders", "");
              v.group = v.label.replace("Graders", "Grade");
              break;
            case "subject":
              v.option = v.label;
              v.group = `${v.label} Students`;
              break;
            case "class_period":
              v.option = v.label.replace(" Period", "");
              v.group = v.label;
              break;
            default:
              break;
          }
        });
        variants.bySlug = reduceToObject(variants, (d) => [d.slug, d]);
        return { slug, name, variants };
      })
      .filter(Boolean);
    comparisons.bySlug = reduceToObject(comparisons, (d) => [d.slug, d]);
    return comparisons;
  }
}

export function extractComparisons(ctx, _level, mainObj) {
  let { comparisonFamilies, includeDatalessGroups = INCLUDE_NA_GROUPS } = ctx;
  let {
    target,
    other_config: { core_grade_numbers, distinct_grade_levels },
  } = ctx;

  let families = [];
  let byFamily = {};
  let byVariant = {};
  const hasData = (obj) => obj && obj._results.some((d) => d.score != null);

  comparisonFamilies[_level].forEach((family) => {
    let variantObjects = [];
    let dataAvailable = false;
    family.variants.forEach((variant) => {
      let _results = extractResults(ctx, variant.slug, mainObj);
      let cmpObj = {
        slug: `${mainObj.slug}/${variant.slug}`,
        baseSlug: mainObj.slug,
        __vslug: variant.slug,
        name: `${mainObj.name} (${variant.label})`,
        baseName: mainObj.name,
        compName: variant.label,
        _results,
        _level,
        _is_comparison: true,
      };
      variantObjects.push((byVariant[variant.slug] = cmpObj));
      dataAvailable |= hasData(cmpObj);
    });

    if (0) {
      // DEV: force display of just one comparison family
      dataAvailable =
        family.slug === "subject" || family.slug === "_multifactor_";
    }
    if (dataAvailable || includeDatalessGroups) {
      byFamily[family.slug] = variantObjects;
      if (family.slug !== "_multifactor_") {
        families.push({ ...family });
      }
    }
  });

  let factors = ["subject", "grade"]; // NOTE: must match order of __vslug components
  let multifactorObjects = byFamily["_multifactor_"];
  if ("grade" in byFamily) {
    /*
    Special per-object handling for grade levels:

      1. Generally, always show a "core" set of grades based on target.level
      1b. …but: always auto-suppress grade 4 and/or 5 when no data available!
      2. Also add each other grade group where data is found within.

    See classic logic in `schools.views.compare_object_data`. */

    // NOTE: `core_grades` **6 and up** are always included, since they are expected based on `target.level`
    //       `special_group` contains 4–5, which are each shown individually iff there is data for that grade
    //       `generic_groups` each get included as a _whole_ if one or more of a group's grade levels has data
    let core_grades = core_grade_numbers[target.level].filter((n) => n > 5);
    let [special_group, ...generic_groups] = distinct_grade_levels.map(
      (l) => core_grade_numbers[l],
    );
    special_group.forEach((l) => {
      // split into single-grade "groups" (which can be treated generic-ly)
      generic_groups.push([l]);
    });

    let prefixes = [""];
    let other_factors = factors.filter((k) => k !== "grade");
    if (multifactorObjects)
      other_factors.forEach((optKey) => {
        let optFamily = comparisonFamilies[_level].bySlug[optKey];
        optFamily.variants.forEach((v) => {
          prefixes.push(`${v.slug}-`); // search multifactor prefixes too
        });
      });

    let groups_with_data = generic_groups.filter((gg) =>
      prefixes.some((p) => gg.some((g) => hasData(byVariant[`${p}grade${g}`]))),
    );
    let included_grades = core_grades.concat(...groups_with_data);
    if (0) {
      // DEV: force a recognizable set of grades
      included_grades = [4, 8, 12];
    }

    // fix up main grades comparison family, disaggregation follows below…
    let vslug_set = reduceToObject(included_grades, (g) => [`grade${g}`, true]);
    byFamily["grade"] = byFamily["grade"].filter((d) => vslug_set[d.__vslug]);
    byFamily["grade"].__vslug_set = vslug_set;
  }
  for (const naKey of ["class_period", "gender", "race"]) {
    if (naKey in byFamily) {
      /*
      Simpler per-object handling for class periods:  [now extended to gender/race too…]

        1. If data is available include the class period for that teacher's schedule
        2. Otherwise assume (albeit oversimplified) that no data means not applicable

      Also, no multi-factor is currently needed for C4C data which simplifies further!
      */

      let all_groups = comparisonFamilies[_level].bySlug[naKey].variants;
      let groups_with_data = all_groups.filter((g) =>
        hasData(byVariant[g.slug]),
      );

      // fix up main grades comparison family, disaggregation follows below…
      let vslug_set = reduceToObject(groups_with_data, (g) => [g.slug, true]);
      byFamily[naKey] = byFamily[naKey].filter((d) => vslug_set[d.__vslug]);
      byFamily[naKey].__vslug_set = vslug_set;
    }
  }

  let byComparison = {}; // disagreggations by cmpKey
  if (multifactorObjects) {
    // remove whatever debug name the API gave these objects
    multifactorObjects.forEach((obj) => {
      delete obj.name;
      obj.__finfo = reduceToObject(obj.__vslug.split("-"), (s, i) => [
        factors[i],
        s,
      ]);
    });
    //console.log(multifactorObjects)

    // prep to suppress grade variants if they haven't been included (logic above)
    let gradeSet = "grade" in byFamily ? byFamily["grade"].__vslug_set : {};
    //console.log("gradeSet is", gradeSet)

    factors.forEach((cmpKey) => {
      let cmpFamily = comparisonFamilies[_level].bySlug[cmpKey];

      let byDisaggregation = {};
      let optionFamilies = [];
      factors.forEach((optKey) => {
        if (optKey === cmpKey) return;
        let optFamily = comparisonFamilies[_level].bySlug[optKey];
        if (!optFamily) return;

        if (optKey === "grade") {
          // suppress grade variants in secondary disaggregation options list
          let variants = optFamily.variants.filter((v) => v.slug in gradeSet);
          variants.bySlug = optFamily.variants.bySlug;
          optFamily = Object.assign(Object.create(optFamily), { variants });
        }

        let familyDataAvailable = false;
        optFamily.variants.forEach((optVariant) => {
          let variantDataAvailable = false;
          let variantObjects = multifactorObjects
            .filter(
              (obj0) =>
                obj0.__finfo[cmpKey] &&
                obj0.__finfo[optKey] === optVariant.slug,
            )
            .filter(
              (obj0) => cmpKey !== "grade" || obj0.__finfo[cmpKey] in gradeSet,
            ) // suppress in multi-factor rows
            .map((obj0) => {
              let _cmpSlug = obj0.__finfo[cmpKey];
              let cmpVariant = cmpFamily.variants.bySlug[_cmpSlug];
              if (!cmpVariant)
                throw Error(
                  `Could not find associated comparison variant for ${_cmpSlug}`,
                );
              variantDataAvailable |= hasData(obj0);

              let obj = Object.create(obj0); // core fields shared
              obj.name =
                optKey === "subject" && cmpKey === "grade"
                  ? `${mainObj.name} (${optVariant.group} in ${cmpVariant.group})`
                  : `${mainObj.name} (${optVariant.group} ${cmpVariant.group})`;

              // also give each form its own slug, for e.g. popup graph `lookupObject` purposes
              let variantSlug = `${optVariant.slug}-${cmpVariant.slug}`;
              obj.slug = `${mainObj.slug}/${variantSlug}`;
              byVariant[variantSlug] = obj;

              return obj;
            });

          familyDataAvailable |= variantDataAvailable;
          if (variantDataAvailable || includeDatalessGroups) {
            byDisaggregation[optVariant.slug] = variantObjects;
          }
        });
        if (familyDataAvailable || includeDatalessGroups) {
          optionFamilies.push(optFamily);
        }
      });
      byComparison[cmpKey] = { optionFamilies, byDisaggregation };
    });
  }

  return { families, byFamily, byVariant, byComparison };
}

export function extractResponses({ survey }, d, prefix = "") {
  let item_slug = d.slug; // NOTE: this and `choice_index` below are primarily used for BEL comparison display
  return Object.keys(d.choices)
    .map((s) => +s)
    .sort()
    .map((k, choice_index) => {
      let response = d.choices[k];
      let benchmark = d.data[`syspct${k}`];
      let percents = survey.list.map((year) => {
        let _p = prefix ? `${prefix}_` : "";
        let _s = year.suffix ? `_${year.suffix}` : "";
        return d.data[`${_p}pctcat${k}${_s}`];
      });
      return { response, benchmark, percents, item_slug, choice_index };
    });
}

export function linkObjects(
  { subjectLabels },
  essentials,
  measures,
  items = [],
) {
  function append(objects, parents) {
    Object.keys(objects).map((s) => {
      let obj = objects[s];
      let { parent_slug: ps } = obj;
      if (ps) {
        let parObj = parents[ps];
        if (parObj) parObj._children.push(obj);
        obj._parent = parObj;
      }
      // ~HACK: we also need to fill in all measures object
      if (obj._level === "measures" && obj._alias !== "all_measures")
        measures.all._children.push(obj);
    });
  }
  append(essentials, essentials);
  append(measures, essentials);
  append(items, measures);
}

export function groupNotes(notes) {
  let notesByContextByMeasure = {};
  notes.forEach((d) => {
    let notesByContext = notesByContextByMeasure[d.measure] || {};
    notesByContext[d.context] = d;
    notesByContextByMeasure[d.measure] = notesByContext;
  });
  return notesByContextByMeasure;
}

// TODO: figure out a better way to get NC enums into the next two functions

export function noteContextLabel(NC, note, measure) {
  switch (note.context) {
    case NC.self:
      return "Measure Page";
    case NC.parent_essential:
      return `${measure._parent.name} Page`;
    case NC.all_measures:
      return "All Measures Page";
  }
}

export function noteContextUrl(NC, links, note, measure) {
  let base;
  switch (note.context) {
    case NC.self:
      base = links.measure_template.replace("_PLACEHOLDER_", measure.slug);
      break;
    case NC.parent_essential:
      base = links.essential_template.replace(
        "_PLACEHOLDER_",
        measure._parent.slug,
      );
      break;
    case NC.all_measures:
      base = links.all_measures;
      break;
  }
  return `${base}#?note=${note.measure}/${note.context}`;
}

// for Early Ed
export function generateThemeSlugs(essentials) {
  const COLOR_KEYS = "ROYGBV";
  let slugToKey = reduceToObject(essentials, (e, i) => [e.slug, COLOR_KEYS[i]]);
  return twoWayMapping(slugToKey);
}

export function expandBasicData(basicData) {
  if (basicData.essentials.length) {
    // (BEL pages provide empty array…)
    let essentials = reduceToObject(
      basicData.essentials,
      ([slug, name, code]) => [
        slug,
        { slug, name, _results: { latest: { code } } },
      ],
    );
    essentials.overall._results.latest.rank = {
      overall_long_label: basicData.overall_long_label,
    };
    essentials.overall._children = Object.keys(essentials)
      .filter((s) => s !== "overall")
      .map((s) => essentials[s]);
    if (basicData.survey.product_key === S.ECE) {
      basicData.survey.themeSlugs = generateThemeSlugs(
        essentials.overall._children,
      );
    }
    basicData.essentials = essentials;
    basicData.overall = essentials.overall;
  }
  basicData.notesCount = basicData.notes_count;

  if (basicData.other_config && basicData.other_config.ranges) {
    let resultCtx = rankInfo(
      basicData.survey_ranks,
      basicData.other_config.ranges,
    );
    Object.assign(basicData, resultCtx);
  }
}
